import { useNavigate } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import { useI18n } from '../i18n';
import { statusLabel } from '../i18n/supportLabels';
import { useMyOrders } from '../hooks/useMyOrders';
import { useMyTickets, useReasonPicker } from '../hooks/useSupport';
import { DeskNotice, TICKET_ROW_HEIGHT, TicketRow, supportTime } from '../components/Support';
import { VirtualList } from '../components/VirtualList';
import {
  Button,
  Card,
  EmptyState,
  ListSkeleton,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui';
import { money } from '../utils/money';
import { orderTime } from '../utils/format';

/**
 * Help hub (W16, Account › Help & support): desk status, your chats (newest first), help with a
 * recent order, and "Something else" for topics not tied to one order.
 */
export function HelpPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const tickets = useMyTickets();
  const orders = useMyOrders(true);
  const desk = useReasonPicker(null);
  const recent = orders.rows.slice(0, 3);

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('help.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-5 px-4 pt-4">
        {desk.picker && (
          <DeskNotice
            tone={desk.picker.deskOpen ? 'positive' : 'warning'}
            icon={desk.picker.deskOpen ? 'users' : 'clock'}
            title={desk.picker.deskOpen ? t('help.deskOpen') : t('help.deskClosed')}
            body={t('help.botNote', { hours: desk.picker.deskHoursText })}
          />
        )}

        <section>
          <SectionTitle>{t('help.recentOrders')}</SectionTitle>
          {orders.loading ? (
            <ListSkeleton rows={2} />
          ) : (
            <Card flush>
              <div className="divide-y divide-hair">
                {recent.map((order) => (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => navigate(`/account/help/new?orderId=${order.id}`)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-tile"
                  >
                    <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
                      <Icon name="receipt" size={18} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-ink">
                        {order.outlet.name}
                      </span>
                      <span className="block truncate text-caption text-ink-3 tabular">
                        {t('orders.number', { number: order.number })} · {orderTime(order.placedAt)}{' '}
                        · {money(order.country, order.totalMinor)}
                      </span>
                    </span>
                    <Icon name="chevron-right" size={16} />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/account/help/new')}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-tile"
                >
                  <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
                    <Icon name="info" size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-ink">
                      {t('help.otherTopics')}
                    </span>
                    <span className="block text-caption text-ink-3">{t('help.otherBody')}</span>
                  </span>
                  <Icon name="chevron-right" size={16} />
                </button>
              </div>
            </Card>
          )}
        </section>

        <section>
          <SectionTitle>{t('help.myTickets')}</SectionTitle>
          {tickets.loading ? (
            <ListSkeleton rows={3} />
          ) : tickets.failed ? (
            <Notice
              tone="negative"
              supportRef
              title={t('help.loadFailed')}
              action={
                <Button size="sm" tone="outline" onClick={tickets.retry}>
                  {t('common.retry')}
                </Button>
              }
            />
          ) : tickets.rows.length === 0 ? (
            <EmptyState icon="info" title={t('help.noTickets')}>
              {t('help.noTicketsBody')}
            </EmptyState>
          ) : (
            <VirtualList
              items={tickets.rows}
              rowHeight={TICKET_ROW_HEIGHT}
              getKey={(row) => row.id}
              renderRow={(row) => (
                <TicketRow
                  number={row.number}
                  reason={row.reasonLabel}
                  status={row.status}
                  statusLabel={statusLabel(t, row.status)}
                  lastMessage={row.lastMessage}
                  meta={[
                    row.orderNumber ? t('help.orderLabel', { number: row.orderNumber }) : null,
                    supportTime(row.updatedAt),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  unread={row.unread}
                  unreadLabel={t('help.unread')}
                  onOpen={() => navigate(`/account/help/${row.id}`)}
                />
              )}
            />
          )}
        </section>
      </div>
    </div>
  );
}
